import { useState } from "react";
import { styles } from "../styles";
import BackButton from "../components/BackButton";
import { hasCapability } from "../utils/capabilities";

/**
 * Queue page for jobs that field staff have CLOSED but billing has not
 * yet finalized into an invoice. Closed + !billFinalized = "waiting on
 * the billing dept." This is the actionable list -- once billing
 * finalizes the bill on a row's job, it drops off here and into the
 * Closed Bills archive.
 *
 * Clicking a row opens that job's detail (JobDetailPage / JobCosting)
 * so billing can do the bill work in-place. Sorted oldest closedAt
 * first so the queue clears chronologically.
 *
 * Gated by editJobs cap -- billing dept preset has it; admins always
 * do. The dashboard tile uses the same gate.
 */
export default function ClosedJobsPage({
  jobsRaw = [],
  customers = [],
  role,
  capabilities,
  onOpenJob,
  onBack,
}) {
  const canView = hasCapability(role, capabilities, "viewClosedJobs");
  const [search, setSearch] = useState("");

  const customerName = (id) =>
    customers.find((c) => c.id === id)?.name || "—";
  const locationName = (cId, lId) => {
    const c = customers.find((x) => x.id === cId);
    return c?.locations?.find((l) => l.id === lId)?.name || "";
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleDateString();
    } catch {
      return "—";
    }
  };

  const matchesSearch = (job) => {
    const term = search.trim().toLowerCase();
    if (!term) return true;
    const cust = customers.find((c) => c.id === job.customerId);
    const loc = cust?.locations?.find((l) => l.id === job.locationId);
    const haystack = [
      job.jobNumber,
      job.title,
      job.description,
      cust?.name,
      loc?.name,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return haystack.includes(term);
  };

  // Closed + not finalized -- the billing queue.
  const queue = [...jobsRaw]
    .filter((j) => j.status === "closed" && !j.billFinalized)
    .filter(matchesSearch)
    .sort(
      (a, b) =>
        new Date(a.closedAt || a.updatedAt || 0) -
        new Date(b.closedAt || b.updatedAt || 0)
    );

  if (!canView) {
    return (
      <>
        <BackButton onClick={onBack} />
        <h2>Closed Jobs</h2>
        <p style={{ color: "#666", fontSize: "13px" }}>
          You don't have permission to view closed jobs.
        </p>
      </>
    );
  }

  return (
    <>
      <BackButton onClick={onBack} />
      <h2>Closed Jobs — Awaiting Bill</h2>
      <p style={{ color: "#666", fontSize: "13px", marginTop: 0 }}>
        Jobs that field staff have closed but no invoice has been finalized
        yet. Click a row to open the job and do the bill work.
      </p>

      <input
        style={{ ...styles.input, marginBottom: "12px" }}
        placeholder="Search by job #, customer, or location"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {queue.length === 0 ? (
        <p style={{ color: "#888", fontSize: "13px" }}>
          {search.trim()
            ? "No closed jobs match that search."
            : "Inbox zero — nothing waiting on billing."}
        </p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {queue.map((job) => {
            const loc = locationName(job.customerId, job.locationId);
            return (
              <div
                key={job.id}
                onClick={() => onOpenJob && onOpenJob(job)}
                style={{
                  ...styles.card,
                  cursor: onOpenJob ? "pointer" : "default",
                  display: "flex",
                  gap: "12px",
                  alignItems: "flex-start",
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <strong style={{ color: "#1a5fb4" }}>
                    {job.jobNumber ? `${job.jobNumber} — ` : ""}
                    {job.title || "(untitled job)"}
                  </strong>
                  <div style={{ fontSize: "12px", color: "#666", marginTop: "2px" }}>
                    {customerName(job.customerId)}
                    {loc ? ` · ${loc}` : ""}
                  </div>
                  {job.description && (
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#444",
                        marginTop: "4px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {job.description}
                    </div>
                  )}
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <div
                    style={{
                      fontSize: "11px",
                      color: "#9a6b00",
                      background: "#fff3cd",
                      padding: "2px 8px",
                      borderRadius: "10px",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      display: "inline-block",
                    }}
                  >
                    Awaiting bill
                  </div>
                  <div style={{ fontSize: "11px", color: "#666", marginTop: "4px" }}>
                    Closed {fmtDate(job.closedAt || job.updatedAt)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
